import React, { useEffect, useState } from "react";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import Table from "../../../components/Table";
import MaterialTypography from "../../../components/MaterialTypography";
import { Card, CardHeader, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MaterialButton from "../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import { deleteData, fetchData } from "../../../services";
import Spinner from "../../../components/Spinner";
import { FileUpload, FilterAlt } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage, setRowsPerPage, setTotalPages } from "./store/index";
import { useLoading } from "../../../components/Loading";
import { useSnackbar } from "../../../context/popup";
import BulkUpload from "../../../components/blukUplod";
import GeneralCoverageFilterDialog from "./components/GeneralCoverageFilter";

const GeneralCoverageList = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setCircleLoading } = useLoading();
  const { showSnackbar } = useSnackbar();

  const currentPage = useSelector((state) => state?.generalCoverageStore?.currentPage);
  const rowsPerPage = useSelector((state) => state?.generalCoverageStore?.rowsPerPage);
  const totalPages = useSelector((state) => state?.generalCoverageStore?.totalPages);

  const [coverageData, setCoverageData] = useState([]);
  const [isRefresh, setIsRefresh] = useState(false);
  const [search, setSearch] = useState("");
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
    productId: "",
    policyId: "",
    coverageTypeId: "",
    isActive: "",
  });

  //list api
  const fetchCoverageData = async () => {
    setCircleLoading(true);
    try {
      let url = `/reference/general/coverage?page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}`;
      if(filterData.productId !== "") {
        url += `&productId=${filterData.productId}`;
      }
      //for active and inactive status
      if(filterData.isActive !== "") {
          url += `&isActive=${filterData.isActive}`;
      }
      //policy type
      if(filterData.policyId !== "") {
          url += `&policyTypeId=${filterData.policyId}`;
      }
      //coverage Type
      if(filterData.coverageTypeId !== ""){
        url += `&coverageTypeId=${filterData.coverageTypeId}`;
      }
      const response = await fetchData(url);
      const responseData = await response?.data?.items;
      if(response?.success){
        setCoverageData(responseData);
        dispatch(setTotalPages(response?.data?.totalPages));
      }
      else{
        //throw error
        showSnackbar("Failed to fetch data", "error");
      }     
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar(error?.response?.data?.message || 'Network Error', "error");
    }
    finally {
      setCircleLoading(false);
    }
  };

  useEffect(() => {
    fetchCoverageData();
  }, [isRefresh, currentPage, rowsPerPage, search]);

  const columns = [
    {
      key: "sno",
      label: "S.No",
      renderCell: (row) => row.sno,
    },
    {
      key: "name",
      label: "Coverage Name",
      renderCell: (row) => row.name,
    },
    {
      key: "description",
      label: "Description",
      renderCell: (row) => row.description,
    },
    {
      key: "coverageType",
      label: "Coverage Type",
      renderCell: (row) => row.coverageType?.keyName,
    },
    {
      key: "mandatory",
      label: "Mandatory",
      renderCell: (row) => (
        <span>
          {row?.mandatory ? (
            <span style={{ color: "green", fontWeight: "bold" }}>Yes</span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>No</span>
          )}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: "Created At",
      renderCell: (row) => row.createdAt,
    },
    {
      key: "isActive",
      label: "Status",
      renderCell: (row) => (
        <span>
          {row.isActive ? (
            <span style={{ color: "green", fontWeight: "bold" }}>Active</span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>Inactive</span>
          )}
        </span>
      )
    },
  ];

  const rows = coverageData.map((coverage, index) => ({
    ...coverage,
    sno: index + 1,
  }));

  const handleEdit = (row) => {
    navigate(`/general-master/general-coverage/edit-coverage/${row?.id}`);
  };

  const handleView = (row) => {
    navigate(`/general-master/general-coverage/view-coverage/${row?.id}`);
  };

  //delete api function
  const handleDelete = async (row) => {
      try {
          const response = await deleteData(`/reference/general/coverage-delete`, {ids:[row?.id]});
          setIsRefresh(!isRefresh);
          if(response?.success){
              showSnackbar(response?.message,"success");
          }
          else{
              showSnackbar(response?.message,"error");
          }
      } catch (error) {
          console.error("Error deleting data:", error);
          showSnackbar(error?.response?.data?.message, "error");
      }
  };

  const actions = {
    edit: true,
    view: true,
    delete: true,
    onEdit: (row) => handleEdit(row),
    onView: (row) => handleView(row),
    onDelete: (row) => handleDelete(row),
  };

  const handleNewCoverage = () => {
    navigate("/general-master/general-coverage/add-coverage");
  };

  const handlePaginationChange = (page) => {
      dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
      dispatch(setRowsPerPage(rowsPerPage));
      dispatch(setCurrentPage(1));
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  return (
    <DashboardLayout>
      <CardHeader
        title={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                General Coverage management list
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                List of Coverages that you can get details of the coverage.
              </MaterialTypography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FilterAlt/>}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                >
                  Filter
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FileUpload />}
                onClick={() => setOpenUploadFileDialog(true)}
              >
                Bulk Upload
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleNewCoverage()}
              >
                Add Coverage
              </MaterialButton>
            </Stack>
          </Stack>
        }
      />
      <Card sx={{ mx: 1 }}>
        <Table
          columns={columns}
          data={rows}
          actions={actions}
          pagination={{
              totalPages,
              currentPage,
              onPageChange: (page) => handlePaginationChange(page),
              rowsPerPage,
              onRowsPerPageChange: (rowsPerPage) => handleRowsPerPageChange(rowsPerPage),
              rowsPerPageOptions: [5, 10, 15, 20],
          }}
          getRowKey={(row) => row.id}
          className="custom-table"
          noDataLabel="No General Coverages found"
          handleSearch={handleSearch}
        />        
      </Card>
      <BulkUpload
        open={openUploadFileDialog}
        setOpen={setOpenUploadFileDialog}
        endPoint="/reference/master/bulkUpload/general-coverage"
        fetchList={fetchCoverageData}
      />
      <GeneralCoverageFilterDialog
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
      filterData={filterData}
      setFilterData={setFilterData}
      onApply={() => {
          setAnchorEl(null);   // close popup
          fetchCoverageData();  // call API after filter applied
      }}/>
    </DashboardLayout>
  );
};

export default GeneralCoverageList;
